import { useCampaignOutletContextIfAvailable } from "src/cep/campaigns/campaign-detail/outlet";
import {
  useMessageOutletContextIfAvailable,
  type OutletMessage,
} from "src/cep/messages/message/outlet";
import {
  deriveSurfaceEditability,
  messageSurfaceInput,
  type ContentEditability,
} from "src/cep/messages/editability";
import { getPaletteSeed } from "src/cep/messages/preview-colors";
import { ChannelType, type CepEmailSenderConfig } from "src/types/cep";
import type { TypedContent } from "src/cep/messages/types";

/**
 * Returns the current message, picking from whichever outlet (campaign or message)
 * is mounted. Both contexts expose a compatible `OutletMessage` shape so shared
 * drawers can operate on either without knowing which parent rendered them.
 */
export function useCurrentMessage(): OutletMessage {
  return useCurrentSurface().message;
}

/**
 * Email-specific sender config for the current message. Returns `null` when the
 * current message is not an email channel, so leaf components don't need to
 * discriminate on `channel_type` themselves before reading email fields.
 */
export function useCurrentEmailSenderConfig(): CepEmailSenderConfig | null {
  const message = useCurrentMessage();
  if (message.channel_type !== ChannelType.Email) return null;
  return message.cep_sender_identity?.config ?? null;
}

/** Convenience wrapper around {@link useCurrentSurface} for the common case
 *  of "can the user make structural edits here?" (name rename, the editable
 *  overview layout). */
export function useIsCurrentMessageEditable(): boolean {
  return useCurrentSurface().canEditStructure;
}

/**
 * Editability capabilities for the current message, derived from whichever
 * outlet (campaign or message) is mounted. Single source of truth for
 * `DrawerRouteGuard`, `MessageComposer`, and overview Edit affordances — every
 * call site that needs to gate on editability should consume this rather than
 * re-reading the outlets themselves. The policy itself lives in the pure
 * `deriveSurfaceEditability` (`packages/backend/lib/cep/surface-editability.ts`, re-exported by `cep/messages/editability.ts`); this hook only
 * picks the active outlet and feeds it in.
 *
 * - `canEditStructure`: structural edits allowed (name rename, the full editable
 *   overview layout, and — via `isDraft` — the Draft-only drawers). Campaigns:
 *   the publishable set. Journey messages: `draft`/`resetting`/`staged`.
 *   Standalone: always.
 * - `isDraft`: narrower than `canEditStructure` — strict Draft-equivalent. Gates
 *   the draft-gated drawers (audience/schedule/metadata) and the mount-time
 *   auto-detach persist. Campaigns: strict `Draft`. Journey messages:
 *   `draft`/`resetting`/`staged`. Standalone: always.
 * - `content`: discriminated union — `canEdit` gates the content editor, and
 *   `saveMode` (present only when `canEdit`) decides whether a content save
 *   publishes immediately or stages against an explicit publish. Content is
 *   editable on every journey state; only `active`/`draining` stage.
 *
 * Throws when neither outlet is mounted — silently defaulting either way
 * would mask routing bugs. The guard is wired only on routes nested under
 * one of these outlets, so the throw only fires on programming errors.
 *
 * Returns a fresh object every render (cheap derivation — no `useMemo`).
 * Consumers must destructure the primitives they need (`content.canEdit`, etc.)
 * and never put the returned `content` object into a dependency array.
 */
export function useCurrentSurface(): {
  message: OutletMessage;
  canEditStructure: boolean;
  isDraft: boolean;
  canEditSettings: boolean;
  content: ContentEditability;
  /** True when the message has staged (unpublished) content changes.
   *  Explicit `!= null` rather than truthiness — `draft_version` is an int and
   *  `0` is a legitimate value. Only ever true on staging surfaces (campaigns
   *  past Draft); Draft/journey/standalone keep `draft_version` null. */
  hasStagedChanges: boolean;
  /** The content the editor and previews should read: the staged draft when
   *  one exists, otherwise the published content. Successive saves chain off
   *  this version. */
  editableContent: TypedContent | null;
} {
  const campaignCtx = useCampaignOutletContextIfAvailable();
  const messageCtx = useMessageOutletContextIfAvailable();

  let message: OutletMessage;
  let editability: ReturnType<typeof deriveSurfaceEditability>;
  if (campaignCtx) {
    if (campaignCtx.messages.length > 1) {
      throw new Error(
        "useCurrentSurface does not support multi-message campaigns yet (omnichannel)",
      );
    }
    const campaignMessage = campaignCtx.messages[0];
    if (!campaignMessage) {
      throw new Error("useCurrentSurface: campaign outlet has no messages");
    }
    message = campaignMessage;
    editability = deriveSurfaceEditability({
      trigger: "campaign",
      derivedStatus: campaignCtx.derivedStatus,
    });
  } else if (messageCtx) {
    message = messageCtx.message;
    editability = deriveSurfaceEditability(
      messageSurfaceInput({
        journeyState: messageCtx.journeyState,
        realtimeJourney: messageCtx.journeyInfo?.realtime ?? false,
        campaignInfo: messageCtx.campaignInfo,
      }),
    );
  } else {
    throw new Error(
      "useCurrentSurface must be used within a campaign or message outlet",
    );
  }

  return {
    message,
    canEditStructure: editability.canEditStructure,
    isDraft: editability.isDraft,
    canEditSettings: editability.canEditSettings,
    content: editability.content,
    hasStagedChanges: message.draft_version != null,
    editableContent: getEditableContent(message),
  };
}

/**
 * Palette seed for the current surface. Every panel on a surface must derive
 * its seed through this hook — seeding from `message.id` alone diverges from
 * campaign-seeded panels on campaign-backed messages.
 */
export function useCurrentPaletteSeed(fallbackId?: string): string {
  const campaignCtx = useCampaignOutletContextIfAvailable();
  const messageCtx = useMessageOutletContextIfAvailable();
  return getPaletteSeed({
    campaignId: campaignCtx?.campaign.id ?? messageCtx?.campaignInfo?.id,
    fallbackId: messageCtx?.message.id ?? fallbackId,
  });
}

/**
 * The content a surface should edit/preview: the staged draft when one exists,
 * otherwise the published content. Single source of truth for the
 * `draft_content ?? current_content` rule, shared by {@link useCurrentSurface}
 * and the save-as-template dialog.
 */
export function getEditableContent<C>(message: {
  draft_content: C | null | undefined;
  current_content: C | null | undefined;
}): C | null {
  return message.draft_content ?? message.current_content ?? null;
}

/**
 * The content the current surface should edit/preview: the staged draft when
 * one exists, otherwise the published content. Thin wrapper over
 * {@link useCurrentSurface} for the common read-only case.
 */
export function useCurrentEditableContent(): TypedContent | null {
  return useCurrentSurface().editableContent;
}
